// GoFocus Breathing App
import { trackEvent } from '../analytics.js';

export class GoFocusApp {
  constructor() {
    this.currentMode = 'breathe';
    this.currentStyle = 'circle';
    this.currentTheme = localStorage.getItem('theme') || 'light';
    this.isZenMode = false;
    this.restartTimerOnMouseMove = localStorage.getItem('restartTimerOnMouseMove') === 'true';
    this.restartOnMoveThrottleId = null;
    this.isSoundOn = localStorage.getItem('soundEnabled') === 'true';
    this.isFirstVisit = !localStorage.getItem('hasVisited');
    this.preferredTimer =
      parseInt(localStorage.getItem('preferredTimer')) || (this.isFirstVisit ? 120 : 0); // Default 2min for new users
    this.timerDuration = this.preferredTimer;
    this.timerRemaining = this.preferredTimer;
    this.timerInterval = null;
    this.breathingPhase = 'inhale'; // 'inhale' or 'exhale'
    this.breathingInterval = null;
    this.ambientOscillator = null;
    this.ambientGain = null;
    this.audioContext = null;
    this.emailSubmitted = localStorage.getItem('emailSubmitted') === 'true';
    this.currentAudioFile = parseInt(localStorage.getItem('preferredAudio')) || 1; // Default to first audio file
    this.dailyGoalSessions = parseInt(localStorage.getItem('dailyGoalSessions')) || 2;
    this.todaySessions = this.loadTodaySessions();
    this.deferredPrompt = null; // For PWA install

    this.audioFiles = {
      1: '/mp3/birds-tree-and-wind-in-middle-of-forest-1-353656.mp3',
      2: '/mp3/birds-tree-and-wind-in-middle-of-forest-2-353655.mp3',
      3: '/mp3/birds-tree-and-wind-in-middle-of-forest-3-353654.mp3',
    };

    this.modeDurations = {
      breathe: { inhale: 4, exhale: 4 },
      reset: { inhale: 3, exhale: 3 },
      meditate: { inhale: 6, exhale: 6 },
    };

    this.init();
  }

  init() {
    // Setup theme first to prevent flash
    this.setupTheme();

    // Wait a frame to ensure theme is applied, then initialize
    requestAnimationFrame(() => {
      this.setupEventListeners();
      this.applyUrlParams();
      this.setupAudio();
      this.updateDailyGoal();
      this.updateWhyNowHint();

      // Show onboarding for first-time users
      if (this.isFirstVisit) {
        this.showOnboarding();
        trackEvent('onboarding_view');
        // Initialize timer display to show 2m (will be set when onboarding starts)
        this.updateTimerDisplay();
      } else {
        // Set timer preference for returning users
        this.setTimer(this.preferredTimer, false);
        setTimeout(() => {
          this.startBreathing();
          trackEvent('session_start', {
            mode: this.currentMode,
            timer_seconds: this.timerDuration,
            style: this.currentStyle,
            is_returning: true,
          });
          // Auto-start sound if it was enabled previously (after user interaction)
          if (this.isSoundOn) {
            // Wait a bit for audio context to be ready, then start sound
            setTimeout(() => {
              this.startSound(); // Start sound without toggling
            }, 500);
          }
        }, 100);
      }

      // Hide loader after initialization
      this.hideLoader();
    });
  }

  hideLoader() {
    const loader = document.getElementById('app-loader');
    if (loader) {
      loader.classList.add('fade-out');
      setTimeout(() => {
        loader.style.display = 'none';
      }, 300);
    }
  }

  setupAudio() {
    const audio = document.getElementById('ambient-sound');
    if (audio && this.audioFiles[this.currentAudioFile]) {
      // Set up the preferred audio file
      const source = document.createElement('source');
      source.src = this.audioFiles[this.currentAudioFile];
      source.type = 'audio/mpeg';
      audio.appendChild(source);
      audio.load();

      // Set active button state
      document.querySelectorAll('.audio-btn').forEach((btn) => {
        btn.classList.toggle('active', parseInt(btn.dataset.audio) === this.currentAudioFile);
      });
    }

    // Set sound button state based on saved preference
    const soundBtn = document.getElementById('sound-toggle');
    if (soundBtn) {
      soundBtn.classList.toggle('active', this.isSoundOn);
    }
  }

  setupTheme() {
    document.documentElement.setAttribute('data-theme', this.currentTheme);
    const themeBtn = document.getElementById('theme-toggle');
    if (themeBtn) {
      const iconSpan = themeBtn.querySelector('span:first-child');
      if (iconSpan) {
        iconSpan.textContent = this.currentTheme === 'dark' ? '☀️' : '🌙';
      }
    }
  }

  setupEventListeners() {
    // Helper for touch + click: prevents double-fire when both events occur (e.g. some touch devices)
    const addTouchListener = (element, handler) => {
      if (!element) return;
      let lastHandled = 0;
      const wrapped = (e) => {
        const now = Date.now();
        if (now - lastHandled < 400) return;
        lastHandled = now;
        handler(e);
      };
      element.addEventListener('click', wrapped);
      element.addEventListener(
        'touchstart',
        (e) => {
          e.preventDefault();
          wrapped(e);
        },
        { passive: false }
      );
    };

    // Mode buttons
    document.querySelectorAll('.mode-btn').forEach((btn) => {
      addTouchListener(btn, (e) => {
        this.setMode(e.target.dataset.mode || e.currentTarget.dataset.mode);
      });
    });

    // Settings toggle
    const settingsToggle = document.getElementById('settings-toggle');
    if (settingsToggle) {
      addTouchListener(settingsToggle, () => {
        this.toggleSettings();
      });
    }

    // Timer buttons
    document.querySelectorAll('.timer-btn').forEach((btn) => {
      addTouchListener(btn, (e) => {
        this.setTimer(parseInt(e.target.dataset.duration || e.currentTarget.dataset.duration));
      });
    });

    // Style buttons
    document.querySelectorAll('.style-btn').forEach((btn) => {
      addTouchListener(btn, (e) => {
        this.setStyle(e.target.dataset.style || e.currentTarget.dataset.style);
      });
    });

    // Audio buttons
    document.querySelectorAll('.audio-btn').forEach((btn) => {
      addTouchListener(btn, (e) => {
        this.setAudioFile(parseInt(e.target.dataset.audio || e.currentTarget.dataset.audio));
      });
    });

    // Theme toggle
    const themeToggle = document.getElementById('theme-toggle');
    if (themeToggle) {
      addTouchListener(themeToggle, () => {
        this.toggleTheme();
      });
    }

    // Zen mode toggle
    const zenToggle = document.getElementById('zen-toggle');
    if (zenToggle) {
      addTouchListener(zenToggle, () => {
        this.toggleZenMode();
      });
    }

    // Sound toggle
    const soundToggle = document.getElementById('sound-toggle');
    if (soundToggle) {
      addTouchListener(soundToggle, () => {
        this.toggleSound();
      });
    }

    // Restart on move toggle
    const restartOnMoveToggle = document.getElementById('restart-on-move-toggle');
    if (restartOnMoveToggle) {
      restartOnMoveToggle.classList.toggle('active', this.restartTimerOnMouseMove);
      addTouchListener(restartOnMoveToggle, () => {
        this.toggleRestartOnMove();
      });
    }

    // Mouse/touch move: restart timer when setting is on and session is timed (throttled)
    const RESTART_ON_MOVE_THROTTLE_MS = 400;
    const onPointerMove = () => {
      if (!this.restartTimerOnMouseMove || this.timerDuration <= 0 || this.timerInterval === null) return;
      if (this.restartOnMoveThrottleId !== null) return;
      this.timerRemaining = this.timerDuration;
      this.updateTimerDisplay();
      this.restartOnMoveThrottleId = setTimeout(() => {
        this.restartOnMoveThrottleId = null;
      }, RESTART_ON_MOVE_THROTTLE_MS);
    };
    document.addEventListener('mousemove', onPointerMove);
    document.addEventListener('touchmove', onPointerMove, { passive: true });

    // Onboarding
    const startOnboarding = document.getElementById('start-onboarding');
    if (startOnboarding) {
      startOnboarding.addEventListener('click', () => {
        this.hideOnboarding();
        this.setTimer(120, true); // Start with 2 minutes and save as preference
        setTimeout(() => this.startBreathing(), 100);
        trackEvent('onboarding_complete', { timer_seconds: 120 });
        trackEvent('session_start', {
          mode: this.currentMode,
          timer_seconds: 120,
          style: this.currentStyle,
          is_returning: false,
        });
      });
    }

    // Email form: Enter key submits
    const emailForm = document.getElementById('email-form');
    if (emailForm) {
      emailForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submitEmail();
      });
    }

    // Session complete: bind each button directly so touch and click both work
    const skipEmailBtn = document.getElementById('skip-email');
    if (skipEmailBtn) {
      // No throttle for skip-email so the first tap always works (throttle could block it after dialog open)
      const onSkipEmail = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.logSessionCompleteChoice('skip_email');
        this.skipEmailCapture();
      };
      skipEmailBtn.addEventListener('click', onSkipEmail);
      skipEmailBtn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        onSkipEmail(e);
      }, { passive: false });
    }
    const emailSubmitBtn = document.getElementById('email-submit-btn');
    if (emailSubmitBtn) {
      addTouchListener(emailSubmitBtn, (e) => {
        e.preventDefault();
        this.submitEmail();
      });
    }
    document.querySelectorAll('#session-complete-dialog .mood-btn').forEach((btn) => {
      addTouchListener(btn, (e) => {
        e.preventDefault();
        const mood = e.currentTarget.dataset.mood;
        if (mood) {
          this.logSessionCompleteChoice('mood', { mood });
          this.saveMood(mood);
          document.querySelectorAll('#session-complete-dialog .mood-btn').forEach((b) => b.classList.remove('selected'));
          e.currentTarget.classList.add('selected');
        }
      });
    });
    const finishSessionBtn = document.getElementById('finish-session');
    if (finishSessionBtn) {
      addTouchListener(finishSessionBtn, (e) => {
        e.preventDefault();
        this.logSessionCompleteChoice('continue');
        this.finishSession();
      });
    }
    const shareSessionBtn = document.getElementById('share-session');
    if (shareSessionBtn) {
      addTouchListener(shareSessionBtn, (e) => {
        e.preventDefault();
        this.logSessionCompleteChoice('share');
        this.shareSession();
      });
    }

    // PWA install prompt: we suppress the browser's default banner and show our own after 3 sessions.
    // The console message "Banner not shown: beforeinstallpromptevent.preventDefault() called" is expected;
    // we call deferredPrompt.prompt() when the user clicks Install in our custom UI.
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      if (this.todaySessions >= 3 && !localStorage.getItem('pwaInstallShown')) {
        this.showInstallPrompt();
      }
    });

    // Close settings when clicking outside
    document.addEventListener('click', (e) => {
      const settingsPanel = document.getElementById('settings-panel');
      const settingsToggle = document.getElementById('settings-toggle');
      if (
        settingsPanel &&
        settingsPanel.classList.contains('open') &&
        !settingsPanel.contains(e.target) &&
        !settingsToggle.contains(e.target)
      ) {
        this.toggleSettings();
      }
    });

    // Fullscreen support
    document.addEventListener('fullscreenchange', () => {
      if (!document.fullscreenElement) {
        this.isZenMode = false;
        document.body.classList.remove('zen-mode');
      }
    });
  }

  /**
   * Apply ?mode= and ?duration= from URL (e.g. from SEO landing pages).
   * mode: calm|focus|balanced|box|478 -> breathe or meditate
   * duration: seconds, e.g. 120 for 2 minutes
   */
  applyUrlParams() {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const modeParam = (params.get('mode') || '').toLowerCase();
    const durationParam = params.get('duration');
    let applied = false;
    if (modeParam) {
      const modeMap = { calm: 'breathe', focus: 'meditate', balanced: 'breathe', box: 'breathe', '478': 'breathe' };
      const appMode = modeMap[modeParam] || 'breathe';
      this.setMode(appMode);
      applied = true;
    }
    if (durationParam !== null && durationParam !== '') {
      const sec = parseInt(durationParam, 10);
      if (!isNaN(sec) && sec >= 0) {
        this.setTimer(sec, true);
        applied = true;
      }
    }
    if (applied && typeof window !== 'undefined' && window.history && window.history.replaceState) {
      const cleanUrl = window.location.pathname || '/';
      window.history.replaceState({}, '', cleanUrl);
    }
  }

  toggleSettings() {
    const settingsPanel = document.getElementById('settings-panel');
    const isOpen = settingsPanel.classList.toggle('open');
    document.body.classList.toggle('settings-open', isOpen);
    if (isOpen) trackEvent('settings_open');
  }

  setMode(mode) {
    // If reset mode, reset the timer but stay in current mode
    if (mode === 'reset') {
      // Reset timer to the original duration (full time remaining)
      this.timerRemaining = this.timerDuration;
      this.updateTimerDisplay();
      // Don't change mode, just reset timer and restart breathing in current mode
      this.startBreathing();
      this.updateWhyNowHint();
      trackEvent('timer_reset', { mode: this.currentMode, timer_seconds: this.timerDuration });
      return; // Exit early, don't change the active button
    }

    this.currentMode = mode;
    document.querySelectorAll('.mode-btn').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.mode === mode);
    });
    document.getElementById('app').setAttribute('data-mode', mode);

    this.startBreathing();
    this.updateWhyNowHint();
    trackEvent('mode_change', { mode });
  }

  updateWhyNowHint() {
    // Top-level status bar no longer uses mode-specific copy,
    // so this is kept as a no-op for backward compatibility.
  }

  setStyle(style) {
    this.currentStyle = style;
    document.querySelectorAll('.style-btn').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.style === style);
    });
    document.querySelectorAll('.breathing-style').forEach((el) => {
      el.classList.toggle('active', el.id === `${style}-style`);
    });
    trackEvent('style_change', { style });
  }

  setAudioFile(audioNumber) {
    this.currentAudioFile = audioNumber;
    localStorage.setItem('preferredAudio', audioNumber.toString());
    trackEvent('ambient_audio_change', { audio_id: audioNumber });

    // Update button states
    document.querySelectorAll('.audio-btn').forEach((btn) => {
      btn.classList.toggle('active', parseInt(btn.dataset.audio) === audioNumber);
    });

    // Update audio source
    const audio = document.getElementById('ambient-sound');
    if (audio && this.audioFiles[audioNumber]) {
      // Remove existing source
      const existingSource = audio.querySelector('source');
      if (existingSource) {
        existingSource.remove();
      }

      // Add new source
      const source = document.createElement('source');
      source.src = this.audioFiles[audioNumber];
      source.type = 'audio/mpeg';
      audio.appendChild(source);

      // Reload audio
      audio.load();

      // If sound is currently playing, restart with new file
      if (this.isSoundOn) {
        audio.play().catch(() => {
          // If play fails, fallback to Web Audio API
          this.createAmbientTone();
        });
      }
    }
  }

  setTimer(duration, savePreference = true) {
    this.timerDuration = duration;
    this.timerRemaining = duration;

    document.querySelectorAll('.timer-btn').forEach((btn) => {
      btn.classList.toggle('active', parseInt(btn.dataset.duration) === duration);
    });

    this.updateTimerDisplay();

    // Save preference
    if (savePreference) {
      localStorage.setItem('preferredTimer', duration.toString());
      this.preferredTimer = duration;
      trackEvent('timer_set', {
        duration_seconds: duration,
        duration_label: duration === 0 ? 'infinite' : `${duration}s`,
      });
    }

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    if (duration > 0) {
      this.timerInterval = setInterval(() => {
        this.timerRemaining--;
        this.updateTimerDisplay();

        if (this.timerRemaining <= 0) {
          clearInterval(this.timerInterval);
          this.timerRemaining = 0;
          this.updateTimerDisplay();
          this.showSessionComplete();
        }
      }, 1000);
    }
  }

  updateTimerDisplay() {
    const display = document.getElementById('timer-display');
    if (!display) {
      return;
    }

    if (this.timerDuration === 0) {
      display.textContent = '∞';
    } else {
      const minutes = Math.floor(this.timerRemaining / 60);
      const seconds = this.timerRemaining % 60;
      display.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  }

  startBreathing() {
    if (this.breathingInterval) {
      clearInterval(this.breathingInterval);
    }

    const durations = this.modeDurations[this.currentMode];
    const totalDuration = durations.inhale + durations.exhale;

    // Update CSS custom property for animation duration
    document.documentElement.style.setProperty('--breath-duration', `${totalDuration}s`);

    // Breathing cue updates
    this.breathingPhase = 'inhale';
    this.updateBreathingCue();

    this.breathingInterval = setInterval(() => {
      this.breathingPhase = this.breathingPhase === 'inhale' ? 'exhale' : 'inhale';
      this.updateBreathingCue();
    }, durations.inhale * 1000);
  }

  updateBreathingCue() {
    const cue = document.getElementById('breathing-cue');
    cue.textContent = this.breathingPhase === 'inhale' ? 'Breathe In' : 'Breathe Out';
    cue.style.opacity = '0.8';

    setTimeout(() => {
      cue.style.opacity = '1';
    }, 100);
  }

  toggleTheme() {
    this.currentTheme = this.currentTheme === 'light' ? 'dark' : 'light';
    localStorage.setItem('theme', this.currentTheme);
    trackEvent('theme_change', { theme: this.currentTheme });
    this.setupTheme();
    // Close settings after theme change
    const settingsPanel = document.getElementById('settings-panel');
    if (settingsPanel && settingsPanel.classList.contains('open')) {
      this.toggleSettings();
    }
  }

  toggleZenMode() {
    this.isZenMode = !this.isZenMode;
    document.body.classList.toggle('zen-mode', this.isZenMode);
    trackEvent('zen_toggle', { enabled: this.isZenMode });

    if (this.isZenMode) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(() => {
          // Fullscreen not available, continue with zen mode
        });
      }
    } else {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    }
  }

  toggleRestartOnMove() {
    this.restartTimerOnMouseMove = !this.restartTimerOnMouseMove;
    localStorage.setItem('restartTimerOnMouseMove', this.restartTimerOnMouseMove.toString());
    trackEvent('restart_on_move_toggle', { enabled: this.restartTimerOnMouseMove });

    const restartOnMoveBtn = document.getElementById('restart-on-move-toggle');
    if (restartOnMoveBtn) {
      restartOnMoveBtn.classList.toggle('active', this.restartTimerOnMouseMove);
    }
  }

  toggleSound() {
    this.isSoundOn = !this.isSoundOn;
    // Save sound preference to localStorage
    localStorage.setItem('soundEnabled', this.isSoundOn.toString());
    trackEvent('sound_toggle', { enabled: this.isSoundOn });

    const soundBtn = document.getElementById('sound-toggle');
    if (soundBtn) {
      soundBtn.classList.toggle('active', this.isSoundOn);
    }

    if (this.isSoundOn) {
      this.startSound();
    } else {
      this.stopSound();
    }
  }

  startSound() {
    const audio = document.getElementById('ambient-sound');
    if (!audio) {
      return;
    }

    // Check if audio file is available (has a source element or src attribute)
    const sourceElement = audio.querySelector('source');
    const hasSourceElement =
      sourceElement !== null && sourceElement.src && sourceElement.src.trim() !== '';
    const hasSrcAttribute =
      audio.src && audio.src.trim() !== '' && !audio.src.includes('about:blank');

    if (hasSourceElement || hasSrcAttribute) {
      // Use audio file - ensure it's loaded
      if (!audio.src || audio.readyState < 2) {
        audio.load(); // Reload if not ready
        // Wait a bit for the load to complete
        setTimeout(() => {
          audio.volume = 0.4;
          audio.play().catch(() => {
            this.createAmbientTone();
          });
        }, 100);
      } else {
        audio.volume = 0.4; // Set comfortable volume (adjust as needed)
        audio.play().catch(() => {
          this.createAmbientTone();
        });
      }
    } else {
      // No audio file, use Web Audio API to generate ambient tone
      this.createAmbientTone();
    }
  }

  stopSound() {
    const audio = document.getElementById('ambient-sound');
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    this.stopAmbientTone();
  }

  createAmbientTone() {
    // Stop any existing tone
    this.stopAmbientTone();

    // Create a pleasant ambient sound using Web Audio API
    try {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Always try to resume the context (browser autoplay policy)
      const resumeContext = () => {
        if (this.audioContext.state === 'suspended') {
          return this.audioContext.resume();
        }
        return Promise.resolve();
      };

      resumeContext()
        .then(() => {
          // Verify context is running
          if (this.audioContext.state === 'running') {
            this.startAmbientTone();
          } else {
            // Try one more time after a short delay
            setTimeout(() => {
              if (this.audioContext && this.audioContext.state === 'running') {
                this.startAmbientTone();
              }
            }, 100);
          }
        })
        .catch(() => {
          // Ignore audio context errors
        });
    } catch {
      // Ignore audio context initialization errors
    }
  }

  startAmbientTone() {
    try {
      if (!this.audioContext) {
        return;
      }

      // Ensure context is running
      if (this.audioContext.state !== 'running') {
        this.audioContext.resume().then(() => {
          this.startAmbientTone();
        });
        return;
      }

      // Create master gain node with audible volume
      const masterGain = this.audioContext.createGain();
      masterGain.gain.value = 0.5; // More audible volume
      masterGain.connect(this.audioContext.destination);

      // Test: verify destination is available
      if (!this.audioContext.destination) {
        return;
      }

      // Create a base tone (low frequency for depth)
      const baseOsc = this.audioContext.createOscillator();
      const baseGain = this.audioContext.createGain();
      baseOsc.type = 'sine';
      baseOsc.frequency.value = 110; // A2 - lower, more calming
      baseGain.gain.value = 1.0; // Full volume
      baseOsc.connect(baseGain);
      baseGain.connect(masterGain);

      // Create a harmonic tone (higher frequency for richness)
      const harmonicOsc = this.audioContext.createOscillator();
      const harmonicGain = this.audioContext.createGain();
      harmonicOsc.type = 'sine';
      harmonicOsc.frequency.value = 220; // A3 - harmonic
      harmonicGain.gain.value = 0.8; // Slightly quieter
      harmonicOsc.connect(harmonicGain);
      harmonicGain.connect(masterGain);

      // Start oscillators immediately
      const now = this.audioContext.currentTime;
      baseOsc.start(now);
      harmonicOsc.start(now);

      // Store for cleanup
      this.ambientOscillator = {
        base: baseOsc,
        harmonic: harmonicOsc,
        masterGain: masterGain,
        baseGain: baseGain,
        harmonicGain: harmonicGain,
      };
    } catch {
      // Fallback to simple tone
      this.createSimpleTone();
    }
  }

  createSimpleTone() {
    try {
      if (!this.audioContext) {
        return;
      }

      if (this.audioContext.state !== 'running') {
        this.audioContext.resume().then(() => {
          this.createSimpleTone();
        });
        return;
      }

      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = 220;
      gainNode.gain.value = 0.3; // More audible

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.start(this.audioContext.currentTime);

      this.ambientOscillator = {
        simple: oscillator,
        gain: gainNode,
      };
    } catch {
      // Failed to create simple tone
    }
  }

  stopAmbientTone() {
    if (this.ambientOscillator) {
      try {
        // Stop all oscillators
        if (this.ambientOscillator.base) {
          this.ambientOscillator.base.stop();
          this.ambientOscillator.base.disconnect();
        }
        if (this.ambientOscillator.harmonic) {
          this.ambientOscillator.harmonic.stop();
          this.ambientOscillator.harmonic.disconnect();
        }
        if (this.ambientOscillator.simple) {
          this.ambientOscillator.simple.stop();
          this.ambientOscillator.simple.disconnect();
        }
        if (this.ambientOscillator.masterGain) {
          this.ambientOscillator.masterGain.disconnect();
        }
        if (this.ambientOscillator.gain) {
          this.ambientOscillator.gain.disconnect();
        }
        if (this.ambientOscillator.baseGain) {
          this.ambientOscillator.baseGain.disconnect();
        }
        if (this.ambientOscillator.harmonicGain) {
          this.ambientOscillator.harmonicGain.disconnect();
        }
        if (this.ambientOscillator.lfoGain) {
          this.ambientOscillator.lfoGain.disconnect();
        }
      } catch {
        // Already stopped or disconnected
      }
      this.ambientOscillator = null;
    }
    if (this.ambientGain) {
      try {
        this.ambientGain.disconnect();
      } catch {
        // Already disconnected
      }
      this.ambientGain = null;
    }
  }

  showOnboarding() {
    const onboarding = document.getElementById('onboarding');
    if (onboarding) {
      onboarding.classList.remove('hidden');
    }
  }

  hideOnboarding() {
    const onboarding = document.getElementById('onboarding');
    if (onboarding) {
      onboarding.classList.add('hidden');
      localStorage.setItem('hasVisited', 'true');
      this.isFirstVisit = false;
    }
  }

  showSessionComplete() {
    trackEvent('session_complete', {
      mode: this.currentMode,
      timer_seconds: this.timerDuration,
      style: this.currentStyle,
    });
    const dialog = document.getElementById('session-complete-dialog');
    if (dialog) {
      dialog.classList.remove('hidden');

      // Show email capture if not already submitted
      const emailCapture = document.getElementById('email-capture');
      const postEmailOptions = document.getElementById('post-email-options');

      if (!this.emailSubmitted && emailCapture && postEmailOptions) {
        emailCapture.classList.remove('hidden');
        postEmailOptions.classList.add('hidden');
      } else {
        if (emailCapture) {
          emailCapture.classList.add('hidden');
        }
        if (postEmailOptions) {
          postEmailOptions.classList.remove('hidden');
          document.querySelectorAll('#session-complete-dialog .mood-btn').forEach((b) => b.classList.remove('selected'));
        }
      }
    }
  }

  hideSessionComplete() {
    const dialog = document.getElementById('session-complete-dialog');
    if (dialog) {
      dialog.classList.add('hidden');
    }
  }

  /** Log every session-complete user choice for analytics (skip, subscribe, mood, continue, share). */
  logSessionCompleteChoice(action, data = {}) {
    trackEvent('session_complete_choice', { action, ...data });
  }

  submitEmail() {
    const emailInput = document.getElementById('email-input');
    const email = emailInput?.value.trim();

    if (email && this.isValidEmail(email)) {
      this.logSessionCompleteChoice('subscribe', { email: email.toLowerCase() });
      trackEvent('email_subscribe', { method: 'session_complete' });
      // Save email locally (in production, send to backend)
      localStorage.setItem('userEmail', email);
      localStorage.setItem('emailSubmitted', 'true');
      this.emailSubmitted = true;

      // Show success and transition to mood check
      const emailCapture = document.getElementById('email-capture');
      const postEmailOptions = document.getElementById('post-email-options');

      if (emailCapture) {
        emailCapture.innerHTML =
          '<p class="email-success">✓ Thanks! Check your email for insights.</p>';
        setTimeout(() => {
          if (emailCapture) {
            emailCapture.classList.add('hidden');
          }
          if (postEmailOptions) {
            postEmailOptions.classList.remove('hidden');
          }
        }, 1500);
      }

      // In production, send to your backend API
      // fetch('/api/subscribe', { method: 'POST', body: JSON.stringify({ email }) });
    } else {
      emailInput?.classList.add('error');
      setTimeout(() => emailInput?.classList.remove('error'), 1000);
    }
  }

  skipEmailCapture() {
    trackEvent('email_skip', { method: 'session_complete' });
    localStorage.setItem('emailSkipped', 'true');
    const emailCapture = document.getElementById('email-capture');
    const postEmailOptions = document.getElementById('post-email-options');

    if (emailCapture) {
      emailCapture.classList.add('hidden');
    }
    if (postEmailOptions) {
      postEmailOptions.classList.remove('hidden');
      document.querySelectorAll('#session-complete-dialog .mood-btn').forEach((b) => b.classList.remove('selected'));
    }
  }

  finishSession() {
    this.hideSessionComplete();
    // Reset timer to preferred duration
    this.setTimer(this.preferredTimer, false);
    // Track session for daily goal
    this.trackSession();
  }

  trackSession() {
    const today = new Date().toISOString().split('T')[0];
    const sessions = JSON.parse(localStorage.getItem('dailySessions') || '{}');
    if (!sessions[today]) {
      sessions[today] = 0;
    }
    sessions[today]++;
    localStorage.setItem('dailySessions', JSON.stringify(sessions));
    this.todaySessions = sessions[today];
    this.updateDailyGoal();
  }

  loadTodaySessions() {
    const today = new Date().toISOString().split('T')[0];
    const sessions = JSON.parse(localStorage.getItem('dailySessions') || '{}');
    return sessions[today] || 0;
  }

  updateDailyGoal() {
    // Goal tracking is internal for now; UI has been simplified.
    const goalTotalEl = document.getElementById('goal-sessions');
    const todayCountEl = document.getElementById('today-sessions-count');

    if (goalTotalEl) {
      goalTotalEl.textContent = this.dailyGoalSessions;
    }
    if (todayCountEl) {
      todayCountEl.textContent = this.todaySessions;
    }
  }

  shareSession() {
    trackEvent('share_session', { method: navigator.share ? 'native_share' : 'clipboard' });
    const affirmations = [
      'Just completed a mindful breathing session with GoFocus ✨',
      'Took 2 minutes to reset my day. Feeling calm and focused 🧘',
      'Daily breathing practice complete. Small wins, big impact 💜',
      "Paused to breathe. Sometimes that's exactly what you need 🌿",
    ];
    const affirmation = affirmations[Math.floor(Math.random() * affirmations.length)];
    const shareText = `${affirmation} Try it: ${window.location.href}`;

    if (navigator.share) {
      navigator
        .share({
          title: 'GoFocus - Mindful Breathing',
          text: affirmation,
          url: window.location.href,
        })
        .catch(() => {
          this.copyToClipboard(shareText);
        });
    } else {
      this.copyToClipboard(shareText);
    }
  }

  copyToClipboard(text) {
    const showCopied = () => {
      const shareBtn = document.getElementById('share-session');
      if (shareBtn) {
        const originalText = shareBtn.textContent;
        shareBtn.textContent = 'Copied!';
        setTimeout(() => {
          shareBtn.textContent = originalText;
        }, 2000);
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(showCopied).catch(showCopied);
    }
  }

  showInstallPrompt() {
    if (!this.deferredPrompt) {
      return;
    }

    // Create a simple install prompt
    const installPrompt = document.createElement('div');
    installPrompt.className = 'install-prompt';
    installPrompt.innerHTML = `
            <div class="install-prompt-content">
                <p>Add GoFocus to your home screen for 1‑tap calm</p>
                <div class="install-prompt-buttons">
                    <button id="install-pwa" class="install-btn">Install</button>
                    <button id="dismiss-install" class="dismiss-btn">Maybe later</button>
                </div>
            </div>
        `;
    document.body.appendChild(installPrompt);

    document.getElementById('install-pwa').addEventListener('click', () => {
      this.deferredPrompt.prompt();
      this.deferredPrompt.userChoice.then((choiceResult) => {
        this.deferredPrompt = null;
        installPrompt.remove();
        if (choiceResult.outcome === 'accepted') {
          localStorage.setItem('pwaInstallShown', 'true');
          trackEvent('pwa_install', { outcome: 'accepted' });
        } else {
          trackEvent('pwa_install', { outcome: 'dismissed' });
        }
      });
    });

    document.getElementById('dismiss-install').addEventListener('click', () => {
      installPrompt.remove();
      localStorage.setItem('pwaInstallShown', 'true');
      trackEvent('pwa_install', { outcome: 'dismissed' });
    });
    trackEvent('pwa_install_prompt_shown');
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  showMoodDialog() {
    // Legacy support - redirect to session complete
    this.showSessionComplete();
  }

  hideMoodDialog() {
    this.hideSessionComplete();
  }

  saveMood(mood) {
    trackEvent('mood_selected', { mood });
    const today = new Date().toISOString().split('T')[0];
    const moods = JSON.parse(localStorage.getItem('moods') || '{}');
    moods[today] = mood;
    localStorage.setItem('moods', JSON.stringify(moods));
  }
}
